import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { api, setTokens } from '~/lib/api'
import { useAuth, apiErrorMessage } from '~/lib/auth'
import { AppHeader } from '~/components/app-header'
import { GoogleAuthButton } from '~/components/google-auth-button'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Notice } from '~/components/ui/notice'

export const Route = createFileRoute('/claim')({
  head: () => ({
    meta: [
      { title: 'Claim your passport — Provenance' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === 'string' ? s.token : '',
  }),
  component: ClaimPage,
})

interface ClaimSummary {
  toEmail: string
  accountExists: boolean
  brand: string
  model: string
  serialNumber: string
}

interface TokenPair {
  accessToken: string
  refreshToken: string
}

function ClaimPage() {
  const { token } = Route.useSearch()
  const { refreshUser } = useAuth()

  const [summary, setSummary] = useState<ClaimSummary | null>(null)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoadError('This claim link is missing its token.')
      setLoading(false)
      return
    }
    let cancelled = false
    api
      .get<ClaimSummary>(`/transfers/claim/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (!cancelled) setSummary(data)
      })
      .catch((err) => {
        if (!cancelled)
          setLoadError(
            apiErrorMessage(err, 'This claim link is invalid or has expired.'),
          )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  async function finishAccept(body: Record<string, unknown>) {
    const { data } = await api.post<TokenPair>('/transfers/claim/accept', {
      token,
      ...body,
    })
    setTokens(data.accessToken, data.refreshToken)
    await refreshUser()
    setDone(true)
  }

  async function handleAccept(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await finishAccept(
        summary?.accountExists ? {} : { firstName, lastName, password },
      )
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not complete the transfer'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGoogle(credential: string) {
    setError('')
    setSubmitting(true)
    try {
      await finishAccept({ googleIdToken: credential })
    } catch (err) {
      setError(apiErrorMessage(err, 'Google sign-in failed'))
    } finally {
      setSubmitting(false)
    }
  }

  const watch = summary
    ? [summary.brand, summary.model].filter(Boolean).join(' ')
    : ''

  return (
    <>
      <AppHeader />
      <main className="mx-auto flex min-h-[calc(100vh-64px)] max-w-[1320px] items-center justify-center px-[var(--gutter)] py-16">
        <div className="bracket-frame relative w-full max-w-[480px] border border-[var(--line)] bg-surface p-8 sm:p-10">
          <span className="bk-bl" aria-hidden="true" />
          <span className="bk-br" aria-hidden="true" />

          <div className="mb-2 font-mono text-[11px] tracking-[0.16em] uppercase text-accent">
            Provenance · Passport transfer
          </div>

          {loading && (
            <p className="font-mono text-[13px] text-ink-soft">Loading…</p>
          )}

          {!loading && loadError && (
            <>
              <h1 className="mb-6 font-mono text-[28px] font-[500] leading-[1.1] text-ink">
                Claim unavailable
              </h1>
              <Notice>{loadError}</Notice>
              <p className="mt-8 text-center font-mono text-[11px] tracking-[0.06em] text-ink-mute">
                <Link to="/" className="text-accent hover:underline">
                  Back to Provenance
                </Link>
              </p>
            </>
          )}

          {!loading && summary && done && (
            <>
              <h1 className="mb-4 font-mono text-[28px] font-[500] leading-[1.1] text-ink">
                Transfer complete
              </h1>
              <p className="mb-8 text-[14px] leading-[1.6] text-ink-soft">
                The passport for your <span className="text-ink">{watch}</span>{' '}
                is now in your account.
              </p>
              <div className="flex flex-col gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  asChild
                >
                  <Link to="/dpp">View your passport</Link>
                </Button>
                <div className="border-t border-[var(--line)] pt-6 text-center">
                  <p className="mb-3 text-[13px] leading-[1.6] text-ink-soft">
                    Carry your passport with you — get the Provenance mobile app.
                  </p>
                  <Link
                    to="/download"
                    className="font-mono text-[11px] tracking-[0.12em] uppercase text-accent hover:underline"
                  >
                    Get the mobile app →
                  </Link>
                </div>
              </div>
            </>
          )}

          {!loading && summary && !done && (
            <>
              <h1 className="mb-4 font-mono text-[28px] font-[500] leading-[1.1] text-ink">
                Claim your {watch}
              </h1>
              <p className="mb-8 text-[14px] leading-[1.6] text-ink-soft">
                The digital passport for your{' '}
                <span className="text-ink">{watch}</span> (serial{' '}
                <span className="font-mono">{summary.serialNumber}</span>) is
                ready to be transferred to{' '}
                <span className="text-ink">{summary.toEmail}</span>.
              </p>

              <form className="flex flex-col gap-5" onSubmit={handleAccept}>
                {!summary.accountExists && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First name</Label>
                        <Input
                          id="firstName"
                          autoComplete="given-name"
                          required
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          disabled={submitting}
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last name</Label>
                        <Input
                          id="lastName"
                          autoComplete="family-name"
                          required
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          disabled={submitting}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" value={summary.toEmail} disabled />
                    </div>
                    <div>
                      <Label htmlFor="password">Create a password</Label>
                      <Input
                        id="password"
                        type="password"
                        autoComplete="new-password"
                        placeholder="At least 8 characters"
                        minLength={8}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                  </>
                )}

                {summary.accountExists && (
                  <p className="text-[13px] leading-[1.6] text-ink-soft">
                    Accept the transfer to add this passport to your account and
                    sign in.
                  </p>
                )}

                {error && <Notice>{error}</Notice>}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting
                    ? 'Completing…'
                    : summary.accountExists
                      ? 'Accept transfer'
                      : 'Claim & create account'}
                </Button>

                {!summary.accountExists && (
                  <GoogleAuthButton
                    text="continue_with"
                    onError={setError}
                    onCredential={handleGoogle}
                  />
                )}
              </form>
            </>
          )}
        </div>
      </main>
    </>
  )
}
